import Script from "next/script";
import React from "react";
const IUBENDA_SITE_ID = process.env.NEXT_PUBLIC_IUBENDA_SITE_ID;

// Legal-approved banner copy (Sept 2026, CCPA/GDPR update). Iubenda replaces
// `%{cookie_policy_link}` with a link captioned by `cookiePolicyLinkCaption`.
// Button captions live in the banner config below, so the dashboard texts no
// longer apply to them.
// Legal-approved opt-in copy for GDPR/LGPD regions (EU/EEA + UK, CH, BR).
const BANNER_CONTENT_OPT_IN = "We use strictly necessary and additional cookies to provide you with the best online experience. " + "Read our %{cookie_policy_link}.";

/**
 * Notice-at-collection copy for US visitors. CPRA asks for the categories
 * collected, the purposes and whether data is sold or shared — a cookie
 * sentence alone does not cover it. DRAFT: needs Ouster legal sign-off, and
 * it must stay consistent with the notice-at-collection section of the
 * privacy policy (`privacyPolicyNoticeAtCollectionUrl`).
 *
 * Shown to every US state, not just California. Only California has an
 * explicit notice-at-collection duty, but Render's Cloudflare forwards the
 * country and no state (verified on staging), and the bar is notice rather
 * than prior consent, so it costs no measurement anywhere it appears.
 */
const BANNER_CONTENT_OPT_OUT = "We collect device, usage and contact information to operate this site, measure performance and " + "support marketing, and we share it with analytics and advertising partners. You can opt out of " + "the sale or sharing of your personal information at any time. See our %{privacy_policy} and " + "%{cookie_policy_link}.";
const defaultState = {
  necessary: false,
  functionality: false,
  experience: false,
  measurement: false,
  marketing: false,
  isReady: false
};
type IubendaPreferences = {
  purposes?: Record<string, boolean>;
  uspr?: {
    s?: boolean;
    sh?: boolean;
    adv?: boolean;
  };
};
declare global {
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
  interface Window {
    // `cc` is Iubenda's geolocated region: "EU" (EEA + UK), "CH", "BR",
    // a US state such as "US-CA", or another country code.
    _iub?: {
      cc?: string;
      cs?: {
        api?: {
          getPreferences?: () => IubendaPreferences | undefined;
        };
      };
    };
  }
}
export type IubendaPreferenceState = typeof defaultState;
type ResolveInput = {
  preferences?: IubendaPreferences;
  notNeeded?: boolean;
  /** Iubenda's geolocated region, `window._iub.cc`. */
  countryCode?: string;
  /** `navigator.globalPrivacyControl`. */
  globalPrivacyControl?: boolean;
};

/**
 * Map Iubenda's stored choice onto the five purposes the scripts gate on.
 * Pure on purpose: the regime branching is the part that has bitten us, so it
 * is unit-tested in `index.test.ts` rather than only exercised in a browser.
 *
 * - Opt-in regimes (GDPR/LGPD) expose `purposes`: grant only what was accepted.
 * - Opt-out regimes (US state laws) and regions with no consent law allow
 *   tracking until the visitor opts out. This mirrors Iubenda's own Consent
 *   Mode mapping — "sale" (`s`) gates analytics, "sale"/"sharing" gate
 *   marketing — and treats Global Privacy Control as an opt-out, but only
 *   under the US regime where it is legally recognised.
 */
export function resolveIubendaPreferences(input: ResolveInput): IubendaPreferenceState {
  const {
    preferences,
    notNeeded,
    countryCode,
    globalPrivacyControl
  } = input;
  if (preferences?.purposes) {
    return {
      necessary: preferences.purposes["1"] === true,
      functionality: preferences.purposes["2"] === true,
      experience: preferences.purposes["3"] === true,
      measurement: preferences.purposes["4"] === true,
      marketing: preferences.purposes["5"] === true,
      isReady: true
    };
  }

  // e.g. the US, or a region with no consent requirement.
  if (notNeeded || preferences?.uspr) {
    const gpc = /^US/.test(countryCode ?? "") && globalPrivacyControl === true;
    const saleOptOut = gpc || preferences?.uspr?.s === false;
    const sharingOptOut = saleOptOut || preferences?.uspr?.sh === false || preferences?.uspr?.adv === false;
    return {
      necessary: true,
      functionality: true,
      experience: true,
      measurement: !saleOptOut,
      marketing: !sharingOptOut,
      isReady: true
    };
  }

  // Opt-in regime, no choice yet.
  return {
    ...defaultState,
    isReady: true
  };
}
export function useIubendaPreferences() {
  const [state, setState] = React.useState(defaultState);
  React.useEffect(() => {
    const handleIubendaReady = (event: CustomEvent) => {
      // Re-read the stored choice on every event: Iubenda fires
      // `onPreferenceNotNeeded` again after a US visitor saves an opt-out, so
      // the event payload alone would flip the state back to "all granted".
      const preferences: IubendaPreferences | undefined = window._iub?.cs?.api?.getPreferences?.() ?? event.detail.preferences;
      setState(resolveIubendaPreferences({
        preferences,
        notNeeded: Boolean(event.detail.notNeeded),
        countryCode: window._iub?.cc,
        globalPrivacyControl: navigator.globalPrivacyControl
      }));
    };
    window.addEventListener("iubenda_ready", handleIubendaReady as EventListener);
    return () => {
      window.removeEventListener("iubenda_ready", handleIubendaReady as EventListener);
    };
  }, []);
  return state;
}
export function IubendaScript() {
  if (!IUBENDA_SITE_ID) {
    return null;
  }
  return <>
      <Script id="iubenda" dangerouslySetInnerHTML={{
      __html: `
            var _iub = _iub || [];
            var iubendaReady = false;
            var preferences = null;

            _iub.csConfiguration = {
              "applyGdprForCH": true,
              "askConsentAtCookiePolicyUpdate": true,
              "countryDetection": true,
              "enableFadp": true,
              "enableLgpd": true,
              // TCF off: with the IAB framework on, Iubenda ignores
              // banner.content and forces its long TCF notice, which the
              // legal-approved copy replaces. The TCF stub scripts went too.
              "enableTcf": false,
              "enableUspr": true,
              // Set from the country cookie below: US visitors get the
              // notice bar, everyone else the opt-in flow where it applies.
              "showBannerForUS": false,
              "privacyPolicyNoticeAtCollectionUrl": "https://ouster.com/privacy-policy/",
              "floatingPreferencesButtonDisplay": "bottom-right",
              "googleConsentMode": true,
              // Opt-in banner only where Iubenda's geolocation says the law
              // needs one: EU/EEA + UK ("EU"), Switzerland (applyGdprForCH)
              // and Brazil (enableLgpd). US states get the opt-out regime
              // (enableUspr: "Your Privacy Choices" + GPC); other regions get
              // onPreferenceNotNeeded. Was true (worldwide opt-in) from
              // 2026-08-11 to 2026-10, which cost most non-EU measurement.
              "gdprAppliesGlobally": false,
              "lang": "en",
              "lgpdAppliesGlobally": false,
              "perPurposeConsent": true,
              "siteId": ${IUBENDA_SITE_ID},
              "storage": { "useSiteId": true },
              "whitelabel": false,
              "cookiePolicyId": 48799615,
              "banner": {
                "applyStyles": false,
                "acceptButtonDisplay": true,
                "acceptButtonCaption": "Accept All",
                "closeButtonDisplay": false,
                "content": ${JSON.stringify(BANNER_CONTENT_OPT_IN)},
                "cookiePolicyLinkCaption": "Cookie Policy",
                "customizeButtonDisplay": true,
                "customizeButtonCaption": "Go to my cookie choices",
                "explicitWithdrawal": false,
                "listPurposes": false,
                "ownerName": "ouster.com",
                "privacyPolicyUrl": "https://ouster.com/privacy-policy/",
                "position": "float-bottom-center",
                "rejectButtonDisplay": true,
                "rejectButtonCaption": "Reject All",
                "showPurposesToggles": false,
                "showTotalNumberOfProviders": false
              },
              "callback": {
                onReady: function() {
                  iubendaReady = true;
                  const preferences = _iub.cs.api.getPreferences();

                  const event = new CustomEvent("iubenda_ready", {
                    detail: { preferences },
                  });

                  window.dispatchEvent(event);
                },
                onPreferenceNotNeeded: function(prefs) {
                  const event = new CustomEvent("iubenda_ready", {
                    detail: { prefs, notNeeded: true },
                  });
                  window.dispatchEvent(event);
                },
                onPreferenceExpressed: function() {
                  // Re-fire the ready event so consumers (GTM, Warmly,
                  // ExternalVideo, etc.) pick up the new preferences
                  // without a full page reload.
                  if (iubendaReady) {
                    const preferences = _iub.cs.api.getPreferences();
                    const event = new CustomEvent("iubenda_ready", {
                      detail: { preferences },
                    });
                    window.dispatchEvent(event);
                  }
                }
              }
            };
          `
    }} />
      <Script src="//cdn.iubenda.com/cs/gpp/stub.js" strategy="afterInteractive" />
      {/*
        Region-aware copy. Iubenda has no per-region banner text and no
        per-state targeting: `banner.content` is one global string and
        `showBannerForUS` is US-wide. The country comes from the
        `ouster-country` cookie that `proxy.ts` sets from Cloudflare's
        `cf-ipcountry`, so this is a synchronous read with no request in
        front of the consent chain — which matters, because Google only
        counts a consenting visitor if Iubenda's update lands inside
        `wait_for_update`.
         Iubenda still decides the regime from its own geolocation; this only
        picks the wording. If the cookie is missing, the opt-in copy and no
        US bar stand — the stricter of the two.
       */}
      <Script id="iubenda-region" strategy="afterInteractive" dangerouslySetInnerHTML={{
      __html: `
            (function () {
              try {
                var m = document.cookie.match(/(?:^|;\\s*)ouster-country=([A-Z]{2})/);
                if (m && m[1] === "US" && _iub.csConfiguration) {
                  _iub.csConfiguration.banner.content = ${JSON.stringify(BANNER_CONTENT_OPT_OUT)};
                  _iub.csConfiguration.showBannerForUS = true;
                }
              } catch (e) {}

              // Inject the loader here rather than as a sibling <Script>: two
              // afterInteractive scripts have no guaranteed order, and the
              // loader must not start before the override above has run.
              var s = document.createElement("script");
              s.src = "https://cdn.iubenda.com/cs/iubenda_cs.js";
              s.setAttribute("charset", "UTF-8");
              document.head.appendChild(s);
            })();
          `
    }} />
    </>;
}